import { DocumentView } from "@/components/documents/DocumentView";
import { useDocumentResourceFactory } from "@/lib/use-open-document";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Dialog } from "@/components/common/Dialog";
import { Button } from "@/components/design/Button";
import { Pager } from "@/components/design/Pager";
import {
  DocumentContentDocument,
  MyDocumentsDocument,
} from "@/graphql/generated/operations";
import { logApplicationFailure } from "@/lib/user-error";

// Picks existing vault documents for the share-create flow. Picked documents
// are re-uploaded as SharedItem images/files (a deliberate copy for now -
// SharedItem uploads have no reference to vault documents yet).

export interface PickedDocument {
  /**
   * Id des gewählten Dokuments. Additiv ergänzt, damit ein Aufrufer auf das
   * bestehende Dokument VERWEISEN kann, statt seinen Inhalt ein zweites Mal
   * hochzuladen und eine Dublette in der Bibliothek zu erzeugen.
   */
  id: string;
  name: string;
  mimeType: string;
  content: string;
  preview: string;
}

// The list query has no mimeType field, so image filtering goes by file
// extension (same heuristic the Save service page uses for type badges).
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif|svg|bmp|heic|heif|tiff?)$/i;

// Server page size for the walk below; the list itself is shown a page of rows
// at a time, as on the Documents page.
const PAGE_FETCH = 100;
const DOCS_PER_PAGE = 24;

export function DocumentPickerDialog({
  open,
  kind = "file",
  onClose,
  onPick,
  idPrefix,
}: {
  open: boolean;
  kind?: "image" | "file";
  onClose: () => void;
  onPick: (files: PickedDocument[]) => void;
  /** Testanker: Dialog, `-search`, `-item-<documentId>`, `-confirm`. */
  idPrefix?: string;
}) {
  const intl = useIntl();
  const apollo = useApolloClient();
  const createResource = useDocumentResourceFactory();
  // The whole stock, not one page: the image filter below runs in the client, so a
  // truncated list would turn a silent omission into a false claim ("no images").
  const { data, loading, error, fetchMore } = useQuery(MyDocumentsDocument, {
    variables: { input: { first: PAGE_FETCH } },
    skip: !open,
  });
  const pageInfo = data?.documents?.pageInfo;
  useEffect(() => {
    if (open && pageInfo?.hasNextPage && pageInfo.endCursor) {
      void fetchMore({
        variables: { input: { first: PAGE_FETCH, after: pageInfo.endCursor } },
      }).catch(() => {});
    }
  }, [open, pageInfo, fetchMore]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const allDocs = useMemo(() => (data?.documents?.nodes ?? []).map(d => ({ ...d, resource: createResource(d.id, d.fileName ?? d.name ?? "document") })), [data?.documents?.nodes, createResource]);
  // Of the requested kind, then narrowed by name, then cut into pages. Selection
  // and the confirm below read the FULL set, so picks survive both.
  const ofKind =
    kind === "image"
      ? allDocs.filter((d) => IMAGE_EXT.test(d.fileName ?? d.name ?? ""))
      : allDocs;
  const filter = query.trim().toLowerCase();
  const matching = filter
    ? ofKind.filter((d) => (d.name ?? d.fileName ?? "").toLowerCase().includes(filter))
    : ofKind;
  const totalPages = Math.max(1, Math.ceil(matching.length / DOCS_PER_PAGE));
  const pageClamped = Math.min(page, totalPages - 1);
  const docs = matching.slice(
    pageClamped * DOCS_PER_PAGE,
    pageClamped * DOCS_PER_PAGE + DOCS_PER_PAGE,
  );
  // True until every page is in: an empty list means "not yet", not "not there".
  const incomplete = loading || Boolean(pageInfo?.hasNextPage);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const unnamedDocument = intl.formatMessage({
    id: "share.docPicker.unnamedDocument",
    defaultMessage: "Unnamed document",
  });
  const searchLabel = intl.formatMessage({
    id: "share.docPicker.search",
    defaultMessage: "Search documents",
  });

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function close() {
    setSelected(new Set());
    setLoadError(false);
    setQuery("");
    setPage(0);
    onClose();
  }

  async function confirm() {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    setLoadError(false);
    try {
      const picked = await Promise.all(
        // The full set, not the visible page — a pick from page 1 must survive
        // paging to page 3.
        ofKind
          .filter((d) => selected.has(d.id))
          .map(async (d) => {
            const { data: contentData } = await apollo.query({
              query: DocumentContentDocument,
              variables: { id: d.id },
              fetchPolicy: "network-only",
            });
            const file = contentData?.document?.content;
            if (!file?.content) return null;
            const mimeType = file.mimeType ?? "application/octet-stream";
            return {
              id: d.id,
              name: contentData?.document?.fileName?.trim() || d.name?.trim() || unnamedDocument,
              mimeType,
              content: file.content,
              preview: mimeType.startsWith("image/")
                ? `data:${mimeType};base64,${file.content}`
                : "",
            };
          }),
      );
      const files = picked.filter((f): f is PickedDocument => f !== null);
      if (files.length === 0) {
        setLoadError(true);
        return;
      }
      onPick(files);
      close();
    } catch {
      logApplicationFailure("share.document-picker-fetch");
      setLoadError(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      id={idPrefix}
      open={open}
      onClose={close}
      preventClose={busy}
      maxWidth="md"
      title={
        <FormattedMessage id="share.docPicker.title" defaultMessage="Add from my documents" />
      }
    >
      <div data-graphql="MyDocuments, DocumentContent">
        <p className="mb-3 text-smycles-meta text-ink-muted">
          <FormattedMessage
            id="share.docPicker.hint"
            defaultMessage="Selected documents are attached to the post as a copy."
          />
        </p>

        {ofKind.length > 0 ? (
          <div className="box-flat mb-2 flex items-center bg-page px-3 py-1.5">
            <input
              id={idPrefix && `${idPrefix}-search`}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              aria-label={searchLabel}
              placeholder={searchLabel}
              className="flex-1 bg-transparent text-smycles-meta placeholder:text-ink-muted focus:outline-none"
            />
          </div>
        ) : null}

        {incomplete ? (
          <div className="py-6 text-center text-smycles-meta text-ink-muted">…</div>
        ) : error ? (
          <div className="py-6 text-center text-smycles-meta text-red-600">
            <FormattedMessage
              id="share.docPicker.loadFailed"
              defaultMessage="Loading documents failed."
            />
          </div>
        ) : matching.length === 0 ? (
          <div className="py-6 text-center text-smycles-meta text-ink-muted">
            {filter ? (
              <FormattedMessage id="share.docPicker.noMatches" defaultMessage="No matches" />
            ) : kind === "image" ? (
              <FormattedMessage
                id="share.docPicker.emptyImages"
                defaultMessage="No images in your documents yet."
              />
            ) : (
              <FormattedMessage
                id="share.docPicker.empty"
                defaultMessage="No documents in your vault yet."
              />
            )}
          </div>
        ) : (
          <div className="max-h-80 space-y-1 overflow-y-auto p-1">
            {docs.map((d) => {
              const checked = selected.has(d.id);
              return (
                <div key={d.id} className="flex items-center gap-2">
                <DocumentView variant="inline" id={idPrefix && `${idPrefix}-preview-${d.id}`} file={d.resource} previewSrc={d.preview?.content ? `data:${d.preview.mimeType};base64,${d.preview.content}` : null} />
                <button
                  id={idPrefix && `${idPrefix}-item-${d.id}`}
                  type="button"
                  aria-pressed={checked}
                  onClick={() => toggle(d.id)}
                  className={`flex min-w-0 flex-1 items-center gap-3 rounded-md p-2 text-left ring-1 transition ${
                    checked ? "bg-ink/[0.06] ring-accent-decoration" : "ring-ink/5 hover:bg-ink/[0.06]"
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-smycles-meta">
                      {d.name?.trim() || d.fileName?.trim() || unnamedDocument}
                    </span>
                    {d.group?.name ? (
                      <span className="block truncate text-smycles-label text-ink-muted">{d.group.name}</span>
                    ) : null}
                  </span>
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm text-smycles-micro ring-1 transition ${
                      checked ? "bg-ink text-ink ring-ink-strong" : "text-transparent ring-ink/15"
                    }`}
                  >
                    ✓
                  </span>
                </button>
                </div>
              );
            })}
          </div>
        )}

        {loadError ? (
          <div className="mt-3 text-smycles-meta text-red-600">
            <FormattedMessage
              id="share.docPicker.loadFailed"
              defaultMessage="Loading documents failed."
            />
          </div>
        ) : null}

        <Pager page={pageClamped} totalPages={totalPages} onChange={setPage} />

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="secondary" size="compact" onClick={close}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            id={idPrefix && `${idPrefix}-confirm`}
            variant="primary"
            size="compact"
            onClick={() => void confirm()}
            disabled={selected.size === 0 || busy}
            className="disabled:opacity-40"
          >
            {busy ? (
              "…"
            ) : (
              <FormattedMessage
                id="share.docPicker.confirm"
                defaultMessage="Add {count}"
                values={{ count: selected.size }}
              />
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
